import { BASE_URL } from '../../services/endpoints'
import { getInvestorDataFromSS } from './local-storage'
import type { ClientJourneyEventInput } from './client-journey-events'

function createEventId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `audit-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

const QUEUE_STORAGE_KEY = 'credibila_audit_event_queue'
const MAX_RETRY_ATTEMPTS = 5
const MAX_BATCH_SIZE = 50

type QueuedAuditEvent = ClientJourneyEventInput & {
  id: string
  source: 'frontend'
  attempts: number
  createdAt: string
}

function readQueue(): QueuedAuditEvent[] {
  try {
    const raw = localStorage.getItem(QUEUE_STORAGE_KEY)
    if (!raw) {
      return []
    }
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function writeQueue(queue: QueuedAuditEvent[]): void {
  localStorage.setItem(QUEUE_STORAGE_KEY, JSON.stringify(queue))
}

function getAuthHeaders(): Record<string, string> {
  const { authToken } = getInvestorDataFromSS()
  return authToken ? { Authorization: authToken } : {}
}

async function postBatch(events: QueuedAuditEvent[]): Promise<boolean> {
  const headers = getAuthHeaders()
  const endpoint = headers.Authorization
    ? `${BASE_URL}/v1/audit/events/batch`
    : `${BASE_URL}/v1/audit/events/public`

  const payload = headers.Authorization
    ? {
        events: events.map(
          ({ id, eventName, clientId, userId, source, previousState, newState, metadata }) => ({
            id,
            eventName,
            clientId,
            userId,
            source,
            previousState,
            newState,
            metadata,
          }),
        ),
      }
    : events[0]

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
    body: JSON.stringify(payload),
    keepalive: true,
  })

  return response.status === 202 || response.status === 200
}

export function enqueueAuditEvent(event: ClientJourneyEventInput): void {
  const { investorData } = getInvestorDataFromSS()
  const queuedEvent: QueuedAuditEvent = {
    id: createEventId(),
    ...event,
    source: 'frontend',
    userId: event.userId ?? investorData.userId ?? null,
    clientId: event.clientId ?? investorData.investorReference ?? null,
    attempts: 0,
    createdAt: new Date().toISOString(),
    metadata: {
      ...event.metadata,
      ...(investorData.adminEmail
        ? {
            adminEmail: investorData.adminEmail,
            adminRole: investorData.adminRole,
          }
        : {}),
    },
  }

  const queue = readQueue()
  queue.push(queuedEvent)
  writeQueue(queue)
  void flushAuditEventQueue()
}

export async function flushAuditEventQueue(): Promise<void> {
  const queue = readQueue()
  if (queue.length === 0) {
    return
  }

  const remaining: QueuedAuditEvent[] = []

  for (let index = 0; index < queue.length; index += MAX_BATCH_SIZE) {
    const batch = queue.slice(index, index + MAX_BATCH_SIZE)
    try {
      const success = await postBatch(batch)
      if (!success) {
        remaining.push(
          ...batch.map((event) => ({
            ...event,
            attempts: event.attempts + 1,
          })),
        )
      }
    } catch {
      remaining.push(
        ...batch.map((event) => ({
          ...event,
          attempts: event.attempts + 1,
        })),
      )
    }
  }

  writeQueue(remaining.filter((event) => event.attempts < MAX_RETRY_ATTEMPTS))
}

export function flushAuditEventsOnUnload(): void {
  const queue = readQueue()
  if (queue.length === 0) {
    return
  }

  const { authToken } = getInvestorDataFromSS()
  const endpoint = authToken
    ? `${BASE_URL}/v1/audit/events/batch`
    : `${BASE_URL}/v1/audit/events/public`

  const payload = authToken
    ? {
        events: queue.map(
          ({ id, eventName, clientId, userId, source, previousState, newState, metadata }) => ({
            id,
            eventName,
            clientId,
            userId,
            source,
            previousState,
            newState,
            metadata,
          }),
        ),
      }
    : queue[0]

  navigator.sendBeacon(endpoint, new Blob([JSON.stringify(payload)], { type: 'application/json' }))
}

if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'test') {
  window.addEventListener('beforeunload', flushAuditEventsOnUnload)
  window.setInterval(() => {
    void flushAuditEventQueue()
  }, 30000)
}
